import { useState } from "react";
import type { AppSettings } from "@/lib/queries";
import { useUpdateAppSettings } from "@/lib/mutations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkles, RotateCcw, Save } from "lucide-react";
import { DEFAULT_AI_CONTEXTS, getAiContext, type AiContextKey } from "@emr/shared/ai";

const PROMPT_FIELDS: { key: AiContextKey; label: string; settingKey: "aiPromptReferralTo" | "aiPromptReferralReply" | "aiPromptCertificate"; description: string }[] = [
  {
    key: "referralTo",
    label: "Referral To (Outgoing)",
    settingKey: "aiPromptReferralTo",
    description: "System prompt used when generating or polishing a Referral To letter (GP referring a patient to a specialist).",
  },
  {
    key: "referralReply",
    label: "Referral Reply (Incoming)",
    settingKey: "aiPromptReferralReply",
    description: "System prompt used when generating or polishing a Referral Reply letter (specialist replying to the originating doctor).",
  },
  {
    key: "certificate",
    label: "Medical Certificate",
    settingKey: "aiPromptCertificate",
    description: "System prompt used when generating or polishing a medical certificate.",
  },
];

export function AiPromptsCard({ settings, canEdit }: { settings: AppSettings; canEdit: boolean }) {
  const update = useUpdateAppSettings();
  // Initialize each textarea with the *effective* prompt — the clinic override if one is
  // set, otherwise the built-in DEFAULT_AI_CONTEXTS default — so the user always sees the
  // text that is actually being used and can edit it in place.
  const [form, setForm] = useState({
    aiPromptReferralTo: getAiContext("referralTo", settings.aiPromptReferralTo),
    aiPromptReferralReply: getAiContext("referralReply", settings.aiPromptReferralReply),
    aiPromptCertificate: getAiContext("certificate", settings.aiPromptCertificate),
  });

  const save = async () => {
    try {
      await update.mutateAsync({
        aiPromptReferralTo: form.aiPromptReferralTo.trim() || null,
        aiPromptReferralReply: form.aiPromptReferralReply.trim() || null,
        aiPromptCertificate: form.aiPromptCertificate.trim() || null,
      });
    } catch { /* onError shows toast */ }
  };

  const restoreDefault = (key: AiContextKey) => {
    const fieldKey = PROMPT_FIELDS.find((f) => f.key === key)!.settingKey;
    setForm((f) => ({ ...f, [fieldKey]: DEFAULT_AI_CONTEXTS[key] }));
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-serif text-lg text-maroon-700 dark:text-amber-500 flex items-center gap-2"><Sparkles size={16} /> AI Prompts</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {PROMPT_FIELDS.map((field) => {
          // The textarea shows the effective prompt (override or default). "custom" means
          // the clinic has a non-null override saved in the DB; "unsaved" means the textarea
          // content differs from what is currently saved (the override, or the default if
          // no override is set).
          const savedValue = settings[field.settingKey] ?? "";
          const isCustom = !!savedValue.trim();
          const isDirty = form[field.settingKey] !== (isCustom ? savedValue : DEFAULT_AI_CONTEXTS[field.key]);
          return (
            <div key={field.key} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">{field.label}</Label>
                <div className="flex items-center gap-2">
                  {isCustom && (
                    <span className="text-xs text-amber-700 dark:text-amber-500">custom</span>
                  )}
                  {isDirty && (
                    <span className="text-xs text-blue-600">unsaved</span>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    disabled={!canEdit}
                    onClick={() => restoreDefault(field.key)}
                    title="Reset the textarea to the built-in default prompt (you still need to Save to persist)"
                  >
                    <RotateCcw size={12} className="mr-1" /> Restore default
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted">{field.description}</p>
              <Textarea
                value={form[field.settingKey]}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, [field.settingKey]: e.target.value }))}
                rows={6}
                className="font-mono text-xs"
              />
            </div>
          );
        })}
        {canEdit && (
          <Button onClick={save} disabled={update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
            <Save size={14} className="mr-1" /> Save prompts
          </Button>
        )}
        <p className="text-xs text-muted">
          The textareas show the prompt currently in use (your override, or the built-in default if none is set). Edit in place and Save to store your version. Clearing all text and saving reverts to the built-in default. The "Restore default" button resets the textarea to the built-in default without saving.
        </p>
      </CardContent>
    </Card>
  );
}
